import {
  AlertTriangle,
  House,
  LayoutGrid,
  MapPin,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Armchair,
  ArrowUp,
  Award,
  BadgeCheck,
  CalendarDays,
  BadgePercent,
  BatteryCharging,
  Bus,
  Clock,
  Disc,
  Droplet,
  HardHat,
  Lock,
  Phone,
  RotateCcw,
  Smartphone,
  Sparkles,
  Star,
  Wrench,
  BarChart3,
  Bell,
  Bike,
  Bold,
  Box,
  Car,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  CircleAlert,
  CircleCheck,
  CircleHelp,
  ClipboardList,
  Copy,
  CreditCard,
  Eye,
  EyeOff,
  FileText,
  FolderTree,
  Globe,
  GripVertical,
  Heading2,
  Heading3,
  Headphones,
  Heart,
  Image,
  Inbox,
  Info,
  Italic,
  KeyRound,
  Landmark,
  LayoutDashboard,
  LifeBuoy,
  Link2,
  List,
  ListIndentDecrease,
  ListIndentIncrease,
  ListOrdered,
  LoaderCircle,
  LogOut,
  Mail,
  Megaphone,
  Menu,
  Moon,
  Newspaper,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Plus,
  Quote,
  Redo2,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Store,
  Strikethrough,
  Sun,
  Tag,
  Trash2,
  TrendingUp,
  Truck,
  Underline,
  Undo2,
  Unlink,
  Upload,
  User,
  Users,
  Wallet,
  X,
  type LucideProps,
} from "lucide-react";

/**
 * One icon set for every app. Modules refer to icons by name (`<Icon name="store" />`), so the icon
 * library can change in one place.
 */
const ICONS = {
  alert: AlertTriangle,
  "arrow-down": ArrowDown,
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  armchair: Armchair,
  "arrow-up": ArrowUp,
  award: Award,
  "badge-check": BadgeCheck,
  "badge-percent": BadgePercent,
  battery: BatteryCharging,
  bus: Bus,
  calendar: CalendarDays,
  clock: Clock,
  disc: Disc,
  droplet: Droplet,
  "hard-hat": HardHat,
  lock: Lock,
  phone: Phone,
  "rotate-ccw": RotateCcw,
  smartphone: Smartphone,
  sparkles: Sparkles,
  star: Star,
  wrench: Wrench,
  bell: Bell,
  bike: Bike,
  bold: Bold,
  box: Box,
  car: Car,
  cart: ShoppingCart,
  chart: BarChart3,
  check: Check,
  "chevron-down": ChevronDown,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "chevrons-left": ChevronsLeft,
  "chevrons-right": ChevronsRight,
  "circle-alert": CircleAlert,
  "circle-check": CircleCheck,
  clipboard: ClipboardList,
  close: X,
  copy: Copy,
  "credit-card": CreditCard,
  dashboard: LayoutDashboard,
  edit: Pencil,
  eye: Eye,
  "eye-off": EyeOff,
  file: FileText,
  filter: SlidersHorizontal,
  "folder-tree": FolderTree,
  globe: Globe,
  grip: GripVertical,
  heart: Heart,
  home: House,
  grid: LayoutGrid,
  "map-pin": MapPin,
  "heading-2": Heading2,
  "heading-3": Heading3,
  headphones: Headphones,
  help: CircleHelp,
  image: Image,
  inbox: Inbox,
  indent: ListIndentIncrease,
  info: Info,
  italic: Italic,
  key: KeyRound,
  landmark: Landmark,
  "life-buoy": LifeBuoy,
  link: Link2,
  list: List,
  "list-ordered": ListOrdered,
  loader: LoaderCircle,
  logout: LogOut,
  mail: Mail,
  megaphone: Megaphone,
  menu: Menu,
  moon: Moon,
  newspaper: Newspaper,
  outdent: ListIndentDecrease,
  package: Package,
  "panel-close": PanelLeftClose,
  "panel-open": PanelLeftOpen,
  plus: Plus,
  quote: Quote,
  redo: Redo2,
  refresh: RefreshCw,
  search: Search,
  settings: Settings,
  shield: ShieldCheck,
  store: Store,
  strikethrough: Strikethrough,
  sun: Sun,
  tag: Tag,
  trash: Trash2,
  trending: TrendingUp,
  truck: Truck,
  underline: Underline,
  undo: Undo2,
  unlink: Unlink,
  upload: Upload,
  user: User,
  users: Users,
  wallet: Wallet,
} as const;

export type IconName = keyof typeof ICONS;
export const iconNames = Object.keys(ICONS) as IconName[];

export interface IconProps extends Omit<LucideProps, "ref"> {
  name: IconName;
  /** Accessible label; omit for decorative icons next to text. */
  label?: string;
}

export function Icon({ name, label, size = 18, strokeWidth = 2, ...props }: IconProps) {
  const Component = ICONS[name];
  return (
    <Component
      size={size}
      strokeWidth={strokeWidth}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? "img" : undefined}
      {...props}
    />
  );
}
